import { z } from 'zod';

// Mirrors `createTaskInput` from the shared schemas but coerces empty
// strings to undefined for the optional fields so the wire payload is
// minimal. UI inputs default to empty strings (controlled inputs); the
// form layer turns those into omitted keys before submitting.
//
// Ported 1:1 from the legacy front `TaskForm/schema.ts` — including the
// duration regex and the conditional `superRefine` rules that tie the
// timer fields / custom date to whether an itinerary is selected.

const emptyToUndef = (v: unknown): unknown =>
  v === '' || v === null ? undefined : v;

// Legacy duration grammar: any combination of y/mo/w/d/h/m/s/ms, must be
// non-empty when present. Jira-style (e.g. "1d", "1d 1h", "2w 3d").
export const durationRegex =
  /^(\d+y)?\s*(\d+mo)?\s*(\d+w)?\s*(\d+d)?\s*(\d+h)?\s*(\d+m)?\s*(\d+s)?\s*(\d+ms)?$/;

// Inline check for the Duration field. A value is valid when it's a
// non-empty string matching the grammar above (at least one unit).
export const isValidDuration = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed.length > 0 && durationRegex.test(trimmed);
};

export const DURATION_HINT =
  'Use units like 1d, 1d 1h, 2w 3d (y/mo/w/d/h/m/s).';

// True when `iso` parses to a valid instant strictly in the past. An
// unset/empty value is not "past" — required-ness is handled separately.
export const isPastIso = (iso: string | undefined): boolean => {
  if (!iso) return false;
  const t = Date.parse(iso);
  return !Number.isNaN(t) && t < Date.now();
};

export const taskFormSchema = z
  .object({
    title: z
      .string({ message: 'Title is required' })
      .min(1, 'Title is required')
      .max(200),
    assignedAccountId: z.preprocess(emptyToUndef, z.uuid().optional()),
    profile: z.enum(['consultant', 'finance', 'operation']),
    priority: z.enum(['high', 'medium', 'low']),
    duration: z.preprocess(
      emptyToUndef,
      z
        .string()
        .regex(durationRegex, {
          message:
            'Invalid duration format (e.g., 1y 2mo 3w 4d 5h 6m 7s — any combination)',
        })
        .refine((val) => val.trim().length > 0, {
          message: 'Duration must not be empty',
        })
        .optional()
    ),
    timerDirection: z.preprocess(
      emptyToUndef,
      z.enum(['before', 'after']).optional()
    ),
    timerReference: z.enum([
      'start_of_trip',
      'itinerary_creation',
      'last_change_date',
      'custom_date',
    ]),
    description: z.preprocess(emptyToUndef, z.string().max(255).optional()),
    itineraryId: z.preprocess(emptyToUndef, z.uuid().optional()),
    collectionId: z.preprocess(emptyToUndef, z.uuid().optional()),
    flag: z.union([z.literal(0), z.literal(5), z.literal(10)]).optional(),
    // `expireOn` carries the custom date (ISO string). The legacy schema
    // called this `customDate`; we keep the API field name so the form
    // value maps straight onto the create/update payload.
    expireOn: z.preprocess(emptyToUndef, z.string().optional()),
  })
  .superRefine((data, ctx) => {
    const hasItinerary = !!data.itineraryId;
    const isCustomDate = data.timerReference === 'custom_date';

    // A custom Expire On can never be in the past — for both create and
    // edit. Checked before the branch-specific rules so it surfaces even
    // when the date is also required by the active timer path.
    if (isPastIso(data.expireOn)) {
      ctx.addIssue({
        path: ['expireOn'],
        message: 'Expire On must be today or in the future',
        code: 'custom',
      });
    }

    if (isCustomDate) {
      if (!data.expireOn) {
        ctx.addIssue({
          path: ['expireOn'],
          message: 'Custom date is required',
          code: 'custom',
        });
      }
      return;
    }

    if (hasItinerary) {
      if (!data.duration) {
        ctx.addIssue({
          path: ['duration'],
          message: 'Duration is required when itinerary is selected',
          code: 'custom',
        });
      }
      if (!data.timerDirection) {
        ctx.addIssue({
          path: ['timerDirection'],
          message: 'Timer direction is required when itinerary is selected',
          code: 'custom',
        });
      }
      return;
    }

    // No itinerary and not custom-date → accept either an explicit expire
    // date OR a relative timer (duration). The latter is the admin/template
    // case (a collection task with `1d before start_of_trip` and no
    // itinerary, resolved later when cloned). Mirrors legacy's admin rule.
    if (!data.expireOn && !data.duration) {
      ctx.addIssue({
        path: ['expireOn'],
        message:
          'Custom date or duration is required when no itinerary is selected',
        code: 'custom',
      });
    }
  });

export type TaskFormValues = z.infer<typeof taskFormSchema>;

export const defaultTaskFormValues: TaskFormValues = {
  title: '',
  profile: 'consultant',
  priority: 'low',
  // Legacy non-admin default — the form opens on the custom-date path
  // (no itinerary selected) showing the Expire On picker.
  timerReference: 'custom_date',
  duration: '1d',
  timerDirection: 'before',
};
